import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap h-[32px] rounded-[8px] px-[12px] py-[6px] text-[12px] font-normal leading-[19.157px] tracking-[0.24px] transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        brand: "bg-brand-orange text-white hover:bg-hover-brand_orange",
        default:
          "bg-white text-brand-primary-new shadow-sm hover:bg-hover-gray border-[1px] border-border-gray",
        destructive:
          "bg-[#D92D20] text-destructive-foreground shadow-sm hover:bg-[#D92D20]/90",
        outline:
          "border-[1px] border-border-gray bg-white shadow-sm hover:bg-hover-gray hover:text-accent-foreground text-brand-primary-new",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        "sq-outline":
          "squircle squircle-radius-[8px] squircle-smooth-[1] squircle-outline-color-border-gray squircle-outline-[1px] bg-white hover:bg-hover-gray text-brand-primary-800",
      },
      size: {
        default: "",
        sm: "h-7 px-2 py-1 gap-[6px] text-[11px]",
        md: "h-8 px-3 py-2 text-[12px]",
        lg: "h-10 px-8",
        icon: "h-[32px] w-[32px] px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loaderColor?: string;
  loaderSize?: number;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      loaderColor,
      loaderSize,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = isLoading || disabled;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <LoadingSpinner color={loaderColor} size={loaderSize} />
        ) : (
          props.children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
